import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

/**
 * Eara Accordion
 * Purpose: Controlled accordion behavior that adapts to mobile/desktop UX rules
 */
class EaraAccordion extends HTMLElement {
  /** @returns {HTMLDetailsElement} */
  get details() {
    const el = this.querySelector('details');
    if (!(el instanceof HTMLDetailsElement)) {
      throw new Error('[eara-accordion] <details> element missing');
    }
    return el;
  }

  /** @returns {HTMLElement} */
  get summary() {
    const el = this.details.querySelector('summary');
    if (!(el instanceof HTMLElement)) {
      throw new Error('[eara-accordion] <summary> element missing');
    }
    return el;
  }

  // === Configuration flags (set via data-attributes) ===

  get #lockOnMobile() {
    return this.dataset.lockOnMobile === 'true';
  }

  get #lockOnDesktop() {
    return this.dataset.lockOnDesktop === 'true';
  }

  get #escapeCloses() {
    return this.dataset.escapeCloses === 'true';
  }

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#applyDefaultState();

    this.addEventListener('keydown', this.#onKeyDown, { signal });
    this.summary.addEventListener('click', this.#onClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#onBreakpointChange, { signal });
  }

  disconnectedCallback() {
    // Hard stop on all listeners when component is removed
    this.#controller.abort();
  }

  /**
   * Prevents toggling when accordion is locked for current breakpoint
   */
  #onClick = (event) => {
    const isMobile = isMobileBreakpoint();

    if (
      (isMobile && this.#lockOnMobile) ||
      (!isMobile && this.#lockOnDesktop)
    ) {
      event.preventDefault();
    }
  };

  /**
   * Re-evaluate open state when breakpoint changes
   */
  #onBreakpointChange = () => {
    this.#applyDefaultState();
  };

  /**
   * Applies default open state based on breakpoint
   */
  #applyDefaultState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute('open-mobile')) ||
      (!isMobile && this.hasAttribute('open-desktop'));
  }

  /**
   * Accessibility: close accordion with Escape
   */
  #onKeyDown = (event) => {
    if (event.key !== 'Escape' || !this.#escapeCloses) return;

    event.preventDefault();
    this.details.open = false;
    this.summary.focus();
  };
}

if (!customElements.get('eara-accordion')) {
  customElements.define('eara-accordion', EaraAccordion);
}
